import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import { LuArrowLeft, LuPlus, LuBookOpen, LuTrash2, LuFileText, LuCircleHelp, LuTag, LuPencil } from "react-icons/lu";
import { API } from "../App";

const KnowledgeBasePage = ({ auth }) => {
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newItem, setNewItem] = useState({
    title: "",
    content: "",
    category: "general"
  });
  const [editingItem, setEditingItem] = useState(null);

  const categories = [
    { value: "general", label: "General", icon: LuFileText },
    { value: "faq", label: "FAQ", icon: LuCircleHelp },
    { value: "product", label: "Producto", icon: LuTag },
    { value: "service", label: "Servicio", icon: LuTag },
    { value: "policy", label: "Políticas", icon: LuFileText }
  ];

  useEffect(() => {
    fetchData();
  }, [agentId]);

  const fetchData = async () => {
    try {
      const [agentRes, kbRes] = await Promise.all([
        fetch(`${API}/agents/${agentId}`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        }),
        fetch(`${API}/knowledge/${agentId}`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        })
      ]);

      if (agentRes.ok) {
        const agentData = await agentRes.json();
        setAgent(agentData);
      }
      if (kbRes.ok) {
        const kbData = await kbRes.json();
        setItems(kbData);
      }
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const isEditing = !!editingItem;
      const url = isEditing ? `${API}/knowledge/${editingItem.id}` : `${API}/knowledge`;
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(isEditing ? newItem : { agent_id: agentId, ...newItem })
      });

      if (response.ok) {
        const savedItem = await response.json();
        if (isEditing) {
          setItems(items.map(i => i.id === savedItem.id ? savedItem : i));
          toast.success("Conocimiento actualizado");
        } else {
          setItems([...items, savedItem]);
          toast.success("Conocimiento agregado");
        }
        handleCloseDialog();
      } else {
        toast.error("Error al guardar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setNewItem({ title: "", content: "", category: "general" });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setNewItem({
      title: item.title,
      content: item.content,
      category: item.category
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setNewItem({ title: "", content: "", category: "general" });
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("¿Eliminar este conocimiento?")) return;

    try {
      const response = await fetch(`${API}/knowledge/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });

      if (response.ok) {
        setItems(items.filter(i => i.id !== itemId));
        toast.success("Eliminado");
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const getCategoryInfo = (categoryValue) => {
    return categories.find(c => c.value === categoryValue) || categories[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="rounded-xl" data-testid="back-btn">
                <LuArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <LuBookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg text-foreground">Base de Conocimiento</h1>
                <p className="text-xs text-muted-foreground">{agent?.name}</p>
              </div>
            </div>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
            <DialogTrigger asChild>
              <Button
                onClick={handleOpenCreate}
                className="rounded-full bg-primary hover:bg-primary/90 glow-primary"
                data-testid="add-knowledge-btn"
              >
                <LuPlus className="w-5 h-5 mr-2" />
                Agregar
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-foreground">
                  {editingItem ? "Editar Conocimiento" : "Agregar Conocimiento"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Título</Label>
                  <Input
                    placeholder="Ej: Horarios de atención"
                    className="bg-muted/50 border-transparent focus:border-primary rounded-xl"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    required
                    data-testid="kb-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Categoría</Label>
                  <Select
                    value={newItem.category}
                    onValueChange={(value) => setNewItem({ ...newItem, category: value })}
                  >
                    <SelectTrigger className="bg-muted/50 border-transparent rounded-xl" data-testid="kb-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Contenido</Label>
                  <Textarea
                    placeholder="Escribe la información que el agente debe conocer..."
                    className="bg-muted/50 border-transparent focus:border-primary rounded-xl min-h-[150px]"
                    value={newItem.content}
                    onChange={(e) => setNewItem({ ...newItem, content: e.target.value })}
                    required
                    data-testid="kb-content-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={handleCloseDialog}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="rounded-full bg-primary" disabled={saving} data-testid="kb-save-btn">
                    {saving ? "Guardando..." : "Guardar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {items.length === 0 ? (
          <Card className="glass-card border-white/10 border-dashed">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <LuBookOpen className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Sin conocimiento aún
              </h3>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                Agrega FAQs, información de productos y políticas para que tu agente responda mejor
              </p>
              <Button
                onClick={handleOpenCreate}
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                <LuPlus className="w-5 h-5 mr-2" />
                Agregar Conocimiento
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((item, index) => {
              const categoryInfo = getCategoryInfo(item.category);
              const CategoryIcon = categoryInfo.icon;

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="glass-card border-white/10 hover:border-primary/30 transition-all" data-testid={`kb-item-${item.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading font-semibold text-lg text-foreground">
                              {item.title}
                            </h3>
                            <Badge variant="outline" className="border-primary/30 text-primary">
                              <CategoryIcon className="w-3 h-3 mr-1" />
                              {categoryInfo.label}
                            </Badge>
                          </div>
                          <p className="text-muted-foreground whitespace-pre-wrap">{item.content}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-primary shrink-0"
                            onClick={() => handleOpenEdit(item)}
                            data-testid={`kb-edit-${item.id}`}
                          >
                            <LuPencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive shrink-0"
                            onClick={() => handleDeleteItem(item.id)}
                            data-testid={`kb-delete-${item.id}`}
                          >
                            <LuTrash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default KnowledgeBasePage;
